import { createMemo, createSignal, Show, type Component } from 'solid-js';
import { Activity, AlertCircle, CircleSlash, Wifi, WifiOff } from 'lucide-solid';
import { Popover } from '~/shared/ui/Popover';
import { realtimeMetrics, realtimeStatus } from './status-store';

const formatRelative = (ts: number | null): string => {
  if (ts === null) return '—';
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 5) return 'только что';
  if (secs < 60) return `${secs} с`;
  if (secs < 3600) return `${Math.floor(secs / 60)} мин`;
  return `${Math.floor(secs / 3600)} ч`;
};

export const ConnectionStatus: Component = () => {
  const [hovered, setHovered] = createSignal(false);
  const [anchorEl, setAnchorEl] = createSignal<HTMLElement>();

  const tone = createMemo(() => {
    switch (realtimeStatus()) {
      case 'connected':
        return {
          color: 'bg-success',
          ring: 'shadow-[0_0_0_2px_hsl(var(--color-success-h)_var(--color-success-s)_var(--color-success-l)/0.2)]',
          label: 'Онлайн',
          icon: Wifi,
          pulse: false,
        };
      case 'connecting':
      case 'reconnecting':
        return {
          color: 'bg-warning',
          ring: '',
          label: realtimeStatus() === 'connecting' ? 'Соединение…' : 'Восстановление…',
          icon: Activity,
          pulse: true,
        };
      case 'failed':
        return {
          color: 'bg-danger',
          ring: '',
          label: 'Ошибка',
          icon: AlertCircle,
          pulse: false,
        };
      case 'disconnected':
        return {
          color: 'bg-[var(--color-text-tertiary)]',
          ring: '',
          label: 'Оффлайн',
          icon: WifiOff,
          pulse: false,
        };
      default:
        return null;
    }
  });

  return (
    <Show when={tone()}>
      {(t) => {
        const subtitle = createMemo(() => {
          const m = realtimeMetrics();
          if (m.lastEventAt) return `${m.lastEventTopic ?? 'event'} · ${formatRelative(m.lastEventAt)}`;
          if (m.lastError) return m.lastError;
          return '';
        });

        return (
          <div
            ref={setAnchorEl}
            class="flex items-center gap-2 h-9 px-2.5 rounded-md hover:bg-white/6 transition-colors cursor-default select-none"
            title={`SignalR: ${t().label}${subtitle() ? ' · ' + subtitle() : ''}`}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <span class="relative flex items-center justify-center">
              <span class={`w-1.5 h-1.5 rounded-full ${t().color} ${t().ring}`} />
              <Show when={t().pulse}>
                <span
                  class={`absolute inset-0 w-1.5 h-1.5 rounded-full ${t().color} animate-ping opacity-60`}
                />
              </Show>
            </span>
            <span class="text-[11px] text-tertiary tabular hidden sm:inline">
              {t().label}
            </span>

            <Popover open={hovered()} onClose={() => setHovered(false)} anchor={anchorEl()} align="end">
              <div class="glass-strong rounded-md p-4 w-80 animate-pop-in">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-[13px] font-medium">{t().label}</span>
                  <span class="text-[10px] text-tertiary tabular">
                    SignalR
                  </span>
                </div>
                <div class="space-y-1 text-[11px] text-secondary">
                  <Row label="ID соединения">
                    <span class="tabular truncate max-w-[140px]">
                      {realtimeMetrics().connectionId ?? '—'}
                    </span>
                  </Row>
                  <Row label="Подключено">
                    <span>{formatRelative(realtimeMetrics().lastConnectedAt)} назад</span>
                  </Row>
                  <Row label="Последнее событие">
                    <span>{formatRelative(realtimeMetrics().lastEventAt)} назад</span>
                  </Row>
                  <Row label="Топик">
                    <span class="tabular">{realtimeMetrics().lastEventTopic ?? '—'}</span>
                  </Row>
                  <Row label="Всего событий">
                    <span class="tabular">{realtimeMetrics().eventCount}</span>
                  </Row>
                  <Show when={realtimeMetrics().reconnectCount > 0}>
                    <Row label="Восстановлений">
                      <span class="tabular">{realtimeMetrics().reconnectCount}</span>
                    </Row>
                  </Show>
                  <Show when={realtimeMetrics().lastError}>
                    <div class="mt-2 pt-2 border-t border-hairline text-danger flex gap-1 items-start">
                      <CircleSlash class="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>{realtimeMetrics().lastError}</span>
                    </div>
                  </Show>
                </div>
              </div>
            </Popover>
          </div>
        );
      }}
    </Show>
  );
};

const Row: Component<{ label: string; children: unknown }> = (props) => (
  <div class="flex items-center justify-between gap-2">
    <span class="text-tertiary">{props.label}</span>
    <span>{props.children as never}</span>
  </div>
);
